// Copyright: Sean O'Donoghue
// Read program, then filter by session and/or change to local time zone
// acc[date] = acc[date] || {date, title: dayTitle, events: []};

// eslint-disable-next-line
const log = (msg) => process.env.VUE_APP_DEV && console.log(`useProgram: ${msg}`);
import {useRoute} from "vue-router";
import {DateTime} from "luxon";
import stringify from "json-stringify-safe";
import timezoneHistory from "@/assets/history.json";

const timeZoneLocal = Intl.DateTimeFormat().resolvedOptions().timeZone;
const days = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const programs = {};
let [previousEventEndTime, previousEventDay] = ["", ""]; // in reduce loop, remember previous event
let previousEventDayTitle = ""; // overall title for each day (e.g., Masterclass, VIZBI day 1, ...)
let sessionStartEvent = ""; // event for start of new session

async function getProgram(year) {
  if (!year) return {}; // early exit
  try {
    const module = await import(`@/assets/years/${year}/Program/program.json`);
    return module.default;
  } catch (error) {
    console.error(`Fetching program for ${year}: ${error}`);
  }
}
function timeZoneConference(year) {
  // time zone the conference is set in; read from VIZBI history file
  if (year) {
    return timezoneHistory.years[year];
  } else {
    return "";
  }
}
function getNameOfWeekday(weekdayNum) {
  let days = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  if (weekdayNum >= 0 && weekdayNum <= days.length) {
    return days[weekdayNum - 1];
  }
}
function monthNumberToName(monthNumber) {
  var date = new Date();
  date.setMonth(monthNumber - 1);
  return date.toLocaleString("en-US", {
    month: "long"
  });
}
function parseDate(row, options) {
  const {col, previousEventDate} = options;
  if (row[col("Date")]) {
    const date = row[col("Date")];
    log(`Program/Parse: date = ${date}`);
    return {date, previousEventDate: date};
  }
  return {date: previousEventDate, previousEventDate};
}
function calculateEventTime(row, date, options) {
  date = date.split("/");
  if (date.length !== 3) return {}; // (i guess) skip if date not in dd/mm/yyyy format
  const {col, timeZoneConference, timeZoneSelected} = options;
  let eventTime = row[col("Duration/Time")];
  let start, end, duration;
  if (eventTime && eventTime.match("-")) {
    // time range
    [start, end] = eventTime.split(/\s*-\s*/).map((t) => t.split(":"));
    log(`Event start: ${start}, end: ${end}`);
    const commonDateProps = {year: date[2], month: date[1], second: 0};
    start = DateTime.fromObject(
      {...commonDateProps, day: date[0], hour: start[0], minute: start[1]},
      {zone: timeZoneConference}
    );
    end = DateTime.fromObject(
      {...commonDateProps, day: date[0], hour: end[0], minute: end[1]},
      {zone: timeZoneConference}
    );
    if (timeZoneSelected === timeZoneLocal) {
      start = start.toLocal();
      end = end.toLocal();
    }
    return {start, end};
  }
  if (eventTime) {
    duration = parseInt(eventTime);
    if (!previousEventEndTime) {
      console.error(`Need a previous event`);
      return {};
    }
    const start = previousEventEndTime;
    const end = start.plus({minute: duration});
    return {start, end};
  }
  const title = row[col("Talk/Role/Event")];
  if (title.match(/^End of /i)) return {start: previousEventEndTime};
  if (title.match(/^Bus /i)) return {end: ""};
  return {};
}
function setEventProperties(row, col, i) {
  const event = {};
  event.ref = `event-item-${i}`;
  event.video = row[col("Video")];
  event.mode = event.video ? "Recorded video" : row[col("Mode")];
  if (event.video) log(`Found video=${event.video}; mode=${event.mode}`);
  event.title = row[col("Talk/Role/Event")];
  event.name = row[col("Name")];
  event.url = row[col("URL")];
  event.description =
    row[col("Description")] ||
    (row[col("Category")].match(/Breakout/i)
      ? "All VIZBI participants were invited to propose and participate in two <a href='https://en.wikipedia.org/wiki/Unconference'>unconference</a>-style discussion sessions..."
      : "");
  return event;
}
function setEventType(row, col) {
  const event = {};
  const title = row[col("Talk/Role/Event")];
  if (title.match(/^Session/)) {
    event.type = "session";
    event.title = row[col("Category")];
    if (row[col("Name")]) {
      event.subtitle = title;
    } else if (row[col("Venue")]) {
      event.subtitle = "Venue";
      event.venue = row[col("Venue")];
    }
  } else {
    event.type = row[col("Category")].match(/Keynote/i) ? "keynote" : "speaker";
    event.title = title;
  }
  return event;
}
function processRow(acc, row, i, options) {
  if (i === 0) return acc; // Skip program header row
  const {col, session} = options;
  const role = row[col("Talk/Role/Event")];
  let skip = false; // skip if not in focused session (e.g., Masterclass or Showcase)
  let {date} = parseDate(row, options);
  log(`start loop i = ${i}`);
  log(`previousEventEndTime = ${previousEventEndTime}`);
  if (!role) return acc; // Skip 'events' with no 'Talk/Role/Event'
  if (days.includes(role)) previousEventDayTitle = row[col("Category")]; // e.g., VIZBI day 1
  // A normal row (not a new day)
  const event = setEventProperties(row, col, i);
  Object.assign(event, calculateEventTime(row, date, options));
  Object.assign(event, setEventType(row, col));
  if (session && row[col("Category")] !== session) skip = true; // Skip if event not in 'session'
  if (event.type === "session" && !skip) sessionStartEvent = event; // remember session start event
  if (!event.start) return acc;
  const day = getNameOfWeekday(event.start.weekday); // get name of weekday
  previousEventDay = day; // remember for next event
  log(`day = ${day}`);
  previousEventEndTime = event.end ? event.end : event.start; // remember for next event
  if (skip) return acc;
  date = `${day}, ${monthNumberToName(event.start.month)} ${event.start.day}, ${event.start.year}`;
  const title = session || acc[day]?.title || "";
  log(`[day, date, title] = [${day}, ${date}, ${title}]`);
  log(`acc[day] = ${acc[day] || "something"}`);
  acc[day] = acc[day] || {date, events: [], title};
  log(`after assignment, acc[day] = ${JSON.stringify(acc[day])}`);
  if (sessionStartEvent) {
    acc[day].events.push(sessionStartEvent); // add session start event
    sessionStartEvent = "";
  }
  acc[day].events.push(event);
  if (acc[day] && !acc[day].title) {
    acc[day].title = previousEventDayTitle;
  } // set title for the day
  return acc;
}
function updatedProgram(options) {
  log(`updatedProgram()`);
  const {program} = options;
  log(`program = ${JSON.stringify(program)}`);
  if (!program || !program.length || program.length === 0) return {};
  log(`program.length = ${program.length}`);
  const col = (heading) => program[0].indexOf(heading);
  options.col = col;
  // program.forEach((row, i) => processProgramRow(row, i, options));
  const output = program.reduce((acc, row, i) => processRow(acc, row, i, options), {});
  log(`output = ${JSON.stringify(output)}`);
  return output; //$emit("finished", output);
}

export default async function useProgram(options) {
  // https://www.vuemastery.com/blog/coding-better-composables-1-of-5/
  log(`options = ${stringify(options)}`);
  const {timeZoneSelected = ""} = options;
  if (!timeZoneSelected) return {}; // require timeZoneSelected
  const route = useRoute();
  const year = route.params?.year; // get current year
  log(`year = ${year}`);
  const program = programs[year] || (await getProgram(year)); // get from memory or disk
  // log(`program = ${JSON.stringify(program)}`);
  if (Object.keys(program).length === 0) return {}; // early exit if no program
  options.timeZoneConference = timeZoneConference(year);
  options.program = program;
  return updatedProgram(options); // return the program
}
